import { useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { SLOT_MAP } from '../enums/ItemTypeEnum.js';

function ModelViewerBase({ options, character}) {
	// Wrapper is managed by React; mountRef is the ONLY node the external library can mutate.
	const mountRef = useRef(null);
	const instanceRef = useRef(null);

	// Keep a stable reference to options so a new object identity doesn't cause re-init on every re-render
	const optionsRef = useRef({});
	useEffect(() => {
		if (options && options !== optionsRef.current) {
			optionsRef.current = options;
		}
		// If no options provided, keep default empty object once
		if (!options && !optionsRef.current) {
			optionsRef.current = {};
		}
	}, [options]);

	const toSlotNumber = useCallback((slotType) => {
		if (!slotType) return undefined;
		const key = String(slotType).trim().toLowerCase();

		return Number(SLOT_MAP.get(key));
	}, []);

    // Build a stable, minimal signature for the equipment set (slot + item only)
    const itemsSignature = useMemo(() => {
      if (!Array.isArray(character?.equipment)) return 'no-items';
      try {
        const parts = character?.equipment
          .map(it => ({ id: Number(it?.item) || 0, slot: toSlotNumber(it?.slotType) || 0 }))
          .filter(it => it.id > 0 && it.slot > 0)
          .sort((a,b) => (a.slot - b.slot) || (a.id - b.id))
          .map(it => `${it.slot}:${it.id}`);
        return parts.join('|') || 'empty';
      } catch { return 'empty'; }
    }, [character?.equipment, toSlotNumber]);

	const formatItems = useCallback((list) => {
		if (!Array.isArray(list)) return [];

		const result = list.map(it => {
			let itemID = Number(it.item);
			
			switch (itemID) {
				case 5976: // Guild tabard
					itemID = (character.faction === 'Alliance') ? 45574 : 45581;
					break;
				default:
					break;
			}
			if (!Number.isFinite(itemID)) return {}

			const slot = toSlotNumber(it.slotType);			

			return {
				slot, itemID
			};
		});

		// Left:
		// 1 - head
		// 3 - shoulders
		// 15 - back (not rendered?)
		// 5 - chest
		// 4 - shirt
		// 19 - tabard
		// 9 - bracers

		// Right:
		// 10 - hands
		// 6 - waist
		// 7 - legs
		// 8 - boots

		// Middle:
		// 21 - main hand
		// 22 - off hand

		return result.filter(it => [1, 3, 5, 4, 19, 9, 10, 6, 7, 8, 21, 22].includes(it.slot));
	}, [toSlotNumber, character.faction]);

	useEffect(() => {
		let cancelled = false;
		// Capture the current mount element at effect start to avoid races
		const mountEl = mountRef.current;
		let pollTimer = null;
        let idleHandle = null;

		const init = async () => {
			if (cancelled) return false;
			const GlobalModelViewer = (typeof window !== 'undefined') ? window.ModelViewer : undefined;

			const el = mountEl;
			if (!el) return false;
			// Clear previous content
			while (el.firstChild) el.removeChild(el.firstChild);
			const selectorId = el.id || `model_mount_${Math.random().toString(36).slice(2)}`;
			if (!el.id) el.id = selectorId;
			// Build a viewer config object (separate from the incoming character prop)
			let charConfig = optionsRef.current && optionsRef.current.model;
			
			if (!charConfig) {
				charConfig = {
					type: GlobalModelViewer.WOW,
					contentPath: "https://apigearchecker.ddns.net//wmmv/",
					container: window.jQuery('.model'),
					hd: true,
					aspect: 0.78,
					items: character.modelViewerEquipment,
					models: {
						type: GlobalModelViewer.Wow.Types.CHARACTER,
						id: (character.race + character.gender).toLowerCase().replace(' ','')
					}
				};
			}


			try {
				const externalItems = (formatItems(character?.equipment) || []).map(it => [Number(it.slot) || 0, Number(it.item?.displayid || it.item?.entry) || 0]);
				if (!charConfig.items) charConfig.items = externalItems;
				
				let model = new GlobalModelViewer(charConfig);
				instanceRef.current = model;					
			} catch (e) {
				console.error('[ModelViewer] init failed:', e);
			}
			
			return true;
		};

		// Defer heavy initialization until the main thread is idle to improve responsiveness
		const ric = (typeof window !== 'undefined' && window.requestIdleCallback)
			? window.requestIdleCallback
			: (cb) => setTimeout(() => cb({ timeRemaining: () => 50 }), 0);
		idleHandle = ric(async () => {
			const done = await init();
			if (!done && !cancelled) {
				let attempts = 0;
				const maxAttempts = 30;
				pollTimer = setInterval(async () => {
					attempts += 1;
					const d = await init();
					if (d || attempts >= maxAttempts) {
						clearInterval(pollTimer);
						pollTimer = null;
					}
				}, 100);
			}
		});

		return () => {
			cancelled = true;
			if (pollTimer) {
				try { clearInterval(pollTimer); } catch (_) {}
				pollTimer = null;
			}
            // cancel idle callback if supported
            try {
                if (idleHandle && typeof window !== 'undefined') {
                    const cic = window.cancelIdleCallback || clearTimeout;
                    cic(idleHandle);
                }
            } catch (_) {}
			const inst = instanceRef.current;
			try {
				if (inst && typeof inst.destroy === 'function') inst.destroy();
				else if (inst && typeof inst.dispose === 'function') inst.dispose();
			} catch (_) {}
			instanceRef.current = null;
			if (mountEl) {
				try { while (mountEl.firstChild) mountEl.removeChild(mountEl.firstChild); } catch (_) {}
			}
		};
	}, [character, itemsSignature, formatItems])	;

		return (
			<div className="model" ref={mountRef} />
	    );
}

export default memo(ModelViewerBase);